import { FormControl, MenuItem } from '@mui/material';
import { FC, useMemo } from 'react';
import { useQuery } from 'react-query';
import { alphaSort } from '../../helpers';
import { IDropdownResponse } from '../../models';

import { SMBSelect } from '../../components';
import { getBuildDocumentationLenders } from '../../fetch';

interface ILenderDropdown {
  neighborhoodId?: number;
  value: string | number;
  handleChange: (val: string | number) => void;
  isDisabled?: boolean;
  setOptions?: (val: IDropdownResponse[]) => void;
  apiParams?: any;
  isRequired?: boolean;
  inputLabel?: string;
  isEnabled?: boolean;
  name?: string;
  handleClear?: () => void;
}

export const LenderDropdown: FC<ILenderDropdown> = ({
  neighborhoodId,
  inputLabel,
  isDisabled,
  isRequired,
  value,
  handleChange,
  setOptions,
  apiParams,
  name,
  isEnabled = true,
  handleClear
}) => {
  const { isLoading: isLoadingLenders, data: lenderData } = useQuery<IDropdownResponse[], Error>(
    ['lenders', neighborhoodId],
    () => getBuildDocumentationLenders(neighborhoodId),
    {
      notifyOnChangeProps: 'tracked',
      onSuccess: d => setOptions?.(d),
      enabled: isEnabled
    }
  );

  const lenders = useMemo(() => {
    if (!Array.isArray(lenderData)) {
      console.error('Expected an array for data, received:', typeof lenderData);
      return [];
    }
    return lenderData.map(r => ({
      ...r,
      fullName: `${r.description}`
    }));
  }, [lenderData]);

  return (
    <FormControl fullWidth variant="outlined" size="small" required={isRequired}>
      <SMBSelect
        name={name || null}
        disabled={isDisabled || isLoadingLenders}
        label={isLoadingLenders ? 'Loading...' : inputLabel ?? 'Lender'}
        value={value}
        required={isRequired}
        onChange={({ target: { value } }) => {
          handleChange(value);
        }}
        handleClear={() => {
          handleChange('');
          handleClear && handleClear();
        }}
        SelectProps={{
          MenuProps: {
            sx: {
              maxHeight: '30rem'
            }
          }
        }}
      >
        {!isLoadingLenders && lenders && lenders.length === 0 && (
          <MenuItem disabled>No Lenders available</MenuItem>
        )}
        {!isLoadingLenders &&
          lenders &&
          alphaSort(lenders, 'description').map((lender, index) => {
            return (
              <MenuItem key={`${index}`} value={lender.value}>
                {lender.description}
              </MenuItem>
            );
          })}
      </SMBSelect>
    </FormControl>
  );
};
